<script setup lang="ts">
import { ref, watch, nextTick, onMounted, onUnmounted, computed } from 'vue';
import { navigateTo } from '#app';
import { useLearnDocsSearch } from '@/composables/useLearnDocsSearch';
import { t } from '@/i18n';
import { localizedPath, siteLocaleFromRouteParam } from '@/lib/site-locale';

const { query, results, loading, resetSearch } = useLearnDocsSearch();
const route = useRoute();
const siteLocale = computed(() => siteLocaleFromRouteParam(route.params.locale));

const open = ref(false);
const inputRef = ref<HTMLInputElement | null>(null);
const activeIndex = ref(0);
const listRef = ref<HTMLElement | null>(null);

const isMac = computed(() => {
  if (typeof navigator === 'undefined') return false;
  return /Mac|iPod|iPhone|iPad/.test(navigator.userAgent);
});

function show() {
  open.value = true;
  nextTick(() => inputRef.value?.focus());
}

function hide() {
  open.value = false;
  resetSearch();
  activeIndex.value = 0;
}

function go(hit: { slug: string; headingId?: string }) {
  hide();
  const loc = {
    path: localizedPath(siteLocale.value, hit.slug === 'welcome' ? '/learn' : `/learn/${hit.slug}`),
  } as {
    path: string;
    hash?: string;
  };
  if (hit.headingId) loc.hash = `#${hit.headingId}`;
  navigateTo(loc);
}

function onKeydown(event: KeyboardEvent) {
  if (event.key === 'ArrowDown') {
    event.preventDefault();
    activeIndex.value = Math.min(activeIndex.value + 1, results.value.length - 1);
    scrollToActive();
  } else if (event.key === 'ArrowUp') {
    event.preventDefault();
    activeIndex.value = Math.max(activeIndex.value - 1, 0);
    scrollToActive();
  } else if (event.key === 'Enter' && results.value[activeIndex.value]) {
    event.preventDefault();
    go(results.value[activeIndex.value]);
  } else if (event.key === 'Escape') {
    hide();
  }
}

function scrollToActive() {
  nextTick(() => {
    const container = listRef.value;
    if (!container) return;
    const active = container.children[activeIndex.value] as HTMLElement | undefined;
    if (active) active.scrollIntoView({ block: 'nearest' });
  });
}

watch(results, () => {
  activeIndex.value = 0;
});

function onGlobalKeydown(event: KeyboardEvent) {
  if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
    event.preventDefault();
    if (open.value) hide();
    else show();
  }
  if (event.key === '/' && !open.value) {
    const tag = document.activeElement?.tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA' || document.activeElement?.isContentEditable) return;
    event.preventDefault();
    show();
  }
}

onMounted(() => {
  document.addEventListener('keydown', onGlobalKeydown);
});
onUnmounted(() => {
  document.removeEventListener('keydown', onGlobalKeydown);
});

function highlightSnippet(snippet: string, q: string) {
  if (!q || q.length < 1) return snippet;
  const tokens = q.split(/[\s,，、;；]+/).filter((token) => token.length >= 1);
  if (tokens.length === 0) return snippet;
  const pattern = tokens.map((token) => token.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|');
  return snippet.replace(new RegExp(`(${pattern})`, 'gi'), '<mark>$1</mark>');
}
</script>

<template>
  <div class="doc-search-root">
    <button type="button" class="doc-search-trigger" @click="show">
      <svg class="doc-search-icon" viewBox="0 0 20 20" fill="currentColor">
        <path
          fill-rule="evenodd"
          d="M9 3.5a5.5 5.5 0 100 11 5.5 5.5 0 000-11zM2 9a7 7 0 1112.452 4.391l3.328 3.329a.75.75 0 11-1.06 1.06l-3.329-3.328A7 7 0 012 9z"
          clip-rule="evenodd"
        />
      </svg>
      <span class="doc-search-placeholder">{{ t('landingPages.learn.search.placeholder') }}</span>
      <kbd class="doc-search-kbd">{{ isMac ? '⌘' : 'Ctrl' }}K</kbd>
    </button>

    <Teleport to="body">
      <Transition name="doc-search-fade">
        <div v-if="open" class="doc-search-overlay" @mousedown.self="hide">
          <div class="doc-search-modal" @keydown="onKeydown">
            <div class="doc-search-input-wrap">
              <svg class="doc-search-input-icon" viewBox="0 0 20 20" fill="currentColor">
                <path
                  fill-rule="evenodd"
                  d="M9 3.5a5.5 5.5 0 100 11 5.5 5.5 0 000-11zM2 9a7 7 0 1112.452 4.391l3.328 3.329a.75.75 0 11-1.06 1.06l-3.329-3.328A7 7 0 012 9z"
                  clip-rule="evenodd"
                />
              </svg>
              <input
                ref="inputRef"
                v-model="query"
                type="text"
                class="doc-search-input"
                :placeholder="t('landingPages.learn.search.placeholder')"
                autocomplete="off"
                spellcheck="false"
              />
              <button
                v-if="query"
                type="button"
                class="doc-search-clear"
                @click="
                  resetSearch();
                  nextTick(() => inputRef?.focus());
                "
              >
                <svg viewBox="0 0 20 20" fill="currentColor" class="w-4 h-4">
                  <path
                    d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z"
                  />
                </svg>
              </button>
            </div>

            <div v-if="loading" class="doc-search-status">
              {{ t('landingPages.learn.search.loading') }}
            </div>
            <div v-else-if="query.length >= 2 && results.length === 0" class="doc-search-status">
              {{ t('landingPages.learn.search.no_results', { query }) }}
            </div>

            <ul v-if="results.length > 0" ref="listRef" class="doc-search-results">
              <li
                v-for="(hit, i) in results"
                :key="`${hit.slug}-${hit.line}`"
                class="doc-search-hit"
                :class="{ 'doc-search-hit--active': i === activeIndex }"
                @mouseenter="activeIndex = i"
                @click="go(hit)"
              >
                <div class="doc-search-hit-title">
                  <span class="doc-search-hit-chapter">{{ hit.title }}</span>
                  <span v-if="hit.heading && hit.heading !== hit.title" class="doc-search-hit-sep">
                    ›
                  </span>
                  <span
                    v-if="hit.heading && hit.heading !== hit.title"
                    class="doc-search-hit-heading"
                  >
                    {{ hit.heading }}
                  </span>
                </div>
                <div class="doc-search-hit-snippet" v-html="highlightSnippet(hit.snippet, query)" />
              </li>
            </ul>

            <div class="doc-search-footer">
              <span class="doc-search-footer-hint">
                <kbd>↑↓</kbd> {{ t('landingPages.learn.search.footer_hint') }}
              </span>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<style scoped>
.doc-search-root {
  width: 100%;
}

.doc-search-trigger {
  display: flex;
  align-items: center;
  gap: 0.45rem;
  width: 100%;
  padding: 0.45rem 0.65rem;
  border-radius: 10px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  background: rgba(0, 0, 0, 0.02);
  color: rgba(0, 0, 0, 0.4);
  font-size: 0.82rem;
  cursor: pointer;
  transition:
    border-color 0.15s,
    background 0.15s;
}

.doc-search-trigger:hover {
  border-color: rgba(0, 0, 0, 0.14);
  background: rgba(0, 0, 0, 0.04);
}

:deep(.dark) .doc-search-trigger,
:root.dark .doc-search-trigger {
  border-color: rgba(255, 255, 255, 0.08);
  background: rgba(255, 255, 255, 0.03);
  color: rgba(255, 255, 255, 0.4);
}

:deep(.dark) .doc-search-trigger:hover,
:root.dark .doc-search-trigger:hover {
  border-color: rgba(255, 255, 255, 0.14);
  background: rgba(255, 255, 255, 0.06);
}

.doc-search-icon {
  width: 14px;
  height: 14px;
  flex-shrink: 0;
  opacity: 0.55;
}

.doc-search-placeholder {
  flex: 1;
  text-align: left;
}

.doc-search-kbd {
  font-size: 0.7rem;
  font-family: ui-monospace, 'SF Mono', Menlo, monospace;
  padding: 0.12rem 0.35rem;
  border-radius: 5px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  background: rgba(255, 255, 255, 0.7);
  color: rgba(0, 0, 0, 0.4);
  line-height: 1.3;
}

:root.dark .doc-search-kbd {
  border-color: rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.35);
}

.doc-search-overlay {
  position: fixed;
  inset: 0;
  z-index: 9999;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding-top: min(18vh, 140px);
  background: rgba(0, 0, 0, 0.25);
  backdrop-filter: blur(2px);
}

:root.dark .doc-search-overlay {
  background: rgba(0, 0, 0, 0.5);
}

.doc-search-fade-enter-active,
.doc-search-fade-leave-active {
  transition: opacity 0.15s ease;
}
.doc-search-fade-enter-from,
.doc-search-fade-leave-to {
  opacity: 0;
}

.doc-search-modal {
  width: 92vw;
  max-width: 560px;
  max-height: 72vh;
  display: flex;
  flex-direction: column;
  border-radius: 16px;
  background: #fff;
  box-shadow:
    0 24px 48px -12px rgba(0, 0, 0, 0.18),
    0 0 0 1px rgba(0, 0, 0, 0.06);
  overflow: hidden;
}

:root.dark .doc-search-modal {
  background: #1c1c1a;
  box-shadow:
    0 24px 48px -12px rgba(0, 0, 0, 0.5),
    0 0 0 1px rgba(255, 255, 255, 0.08);
}

.doc-search-input-wrap {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

:root.dark .doc-search-input-wrap {
  border-bottom-color: rgba(255, 255, 255, 0.08);
}

.doc-search-input-icon {
  width: 18px;
  height: 18px;
  flex-shrink: 0;
  color: rgba(0, 0, 0, 0.35);
}

:root.dark .doc-search-input-icon {
  color: rgba(255, 255, 255, 0.35);
}

.doc-search-input {
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  font-size: 0.95rem;
  color: rgba(0, 0, 0, 0.9);
  line-height: 1.5;
}

.doc-search-input::placeholder {
  color: rgba(0, 0, 0, 0.32);
}

:root.dark .doc-search-input {
  color: rgba(255, 255, 255, 0.9);
}

:root.dark .doc-search-input::placeholder {
  color: rgba(255, 255, 255, 0.3);
}

.doc-search-clear {
  flex-shrink: 0;
  padding: 0.2rem;
  border-radius: 6px;
  color: rgba(0, 0, 0, 0.35);
  cursor: pointer;
  border: none;
  background: none;
  transition: color 0.12s;
}

.doc-search-clear:hover {
  color: rgba(0, 0, 0, 0.6);
}

:root.dark .doc-search-clear {
  color: rgba(255, 255, 255, 0.35);
}

:root.dark .doc-search-clear:hover {
  color: rgba(255, 255, 255, 0.65);
}

.doc-search-status {
  padding: 2rem 1rem;
  text-align: center;
  font-size: 0.85rem;
  color: rgba(0, 0, 0, 0.4);
}

:root.dark .doc-search-status {
  color: rgba(255, 255, 255, 0.35);
}

.doc-search-results {
  flex: 1;
  overflow-y: auto;
  list-style: none;
  margin: 0;
  padding: 0.35rem;
}

.doc-search-hit {
  padding: 0.55rem 0.75rem;
  border-radius: 10px;
  cursor: pointer;
  transition: background 0.1s;
}

.doc-search-hit--active {
  background: rgba(0, 0, 0, 0.04);
}

:root.dark .doc-search-hit--active {
  background: rgba(255, 255, 255, 0.06);
}

.doc-search-hit-title {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  font-size: 0.82rem;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.82);
  line-height: 1.35;
}

:root.dark .doc-search-hit-title {
  color: rgba(255, 255, 255, 0.85);
}

.doc-search-hit-chapter {
  flex-shrink: 0;
}

.doc-search-hit-sep {
  color: rgba(0, 0, 0, 0.2);
  flex-shrink: 0;
}

:root.dark .doc-search-hit-sep {
  color: rgba(255, 255, 255, 0.2);
}

.doc-search-hit-heading {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.55);
}

:root.dark .doc-search-hit-heading {
  color: rgba(255, 255, 255, 0.5);
}

.doc-search-hit-snippet {
  margin-top: 0.2rem;
  font-size: 0.78rem;
  color: rgba(0, 0, 0, 0.45);
  line-height: 1.55;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

:root.dark .doc-search-hit-snippet {
  color: rgba(255, 255, 255, 0.4);
}

.doc-search-hit-snippet :deep(mark) {
  background: rgba(255, 200, 50, 0.35);
  color: inherit;
  border-radius: 2px;
  padding: 0 0.08em;
}

:root.dark .doc-search-hit-snippet :deep(mark) {
  background: rgba(255, 200, 50, 0.25);
}

.doc-search-footer {
  padding: 0.5rem 1rem;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
  font-size: 0.72rem;
  color: rgba(0, 0, 0, 0.3);
}

:root.dark .doc-search-footer {
  border-top-color: rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.25);
}

.doc-search-footer-hint {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  flex-wrap: wrap;
}

.doc-search-footer-hint kbd {
  font-family: ui-monospace, 'SF Mono', Menlo, monospace;
  font-size: 0.68rem;
  padding: 0.08rem 0.3rem;
  border-radius: 4px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  background: rgba(0, 0, 0, 0.03);
  line-height: 1.4;
}

:root.dark .doc-search-footer-hint kbd {
  border-color: rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.05);
}
</style>
